/**
 * Hardened `localStorage` wrapper.
 *
 * `localStorage` throws on *access*, not just on write:
 *   - Safari private mode and some embedded webviews throw a SecurityError on
 *     any property access.
 *   - "Block third-party cookies / site data" makes `window.localStorage` throw.
 *   - `QuotaExceededError` on write once the origin's ~5 MB budget is full.
 *
 * The journal is read during app boot, so an unguarded access would take the
 * ENTIRE app down for those users. Every path here is guarded, and when storage
 * is unusable we fall back to an in-memory Map: the app then behaves exactly as
 * it does today (no cross-reload persistence) and never throws.
 */

const memoryStore = new Map();

let available = null; // lazily probed, then cached

/**
 * The whole probe — including merely *reaching* `window.localStorage` — has to
 * be inside the try. When site data is blocked, the property getter itself
 * throws, so `!!window.localStorage` in a guard expression is already a crash.
 * That is an app-boot crash, because the journal is read during boot.
 */
function probe() {
  try {
    if (typeof window === "undefined") return false;
    const store = window.localStorage;
    if (!store) return false;
    const probeKey = "__frdm_tx_probe__";
    store.setItem(probeKey, "1");
    store.removeItem(probeKey);
    return true;
  } catch (err) {
    return false;
  }
}

export function isPersistent() {
  if (available === null) {
    available = probe();
    if (!available) {
      // Not an error — a supported degraded mode. Logged once so support can
      // tell "state didn't survive reload" from "state was wrong".
      console.warn(
        "[tx] localStorage unavailable; transaction state will not survive reload"
      );
    }
  }
  return available;
}

export function getItem(key) {
  if (!isPersistent()) return memoryStore.has(key) ? memoryStore.get(key) : null;
  try {
    return window.localStorage.getItem(key);
  } catch (err) {
    // Storage was available at probe time but failed now (quota eviction,
    // permission revoked mid-session). Degrade rather than throw.
    available = false;
    return memoryStore.has(key) ? memoryStore.get(key) : null;
  }
}

/**
 * @param {string} key
 * @param {string} value
 * @param {() => void} [onQuotaExceeded] invoked once before a single retry —
 *        the journal passes its GC here so a full store self-heals.
 * @returns {boolean} whether the value was durably persisted
 */
export function setItem(key, value, onQuotaExceeded) {
  if (!isPersistent()) {
    memoryStore.set(key, value);
    return false;
  }
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (err) {
    if (isQuotaError(err) && typeof onQuotaExceeded === "function") {
      try {
        onQuotaExceeded();
        window.localStorage.setItem(key, value);
        return true;
      } catch (retryErr) {
        // fall through to memory
      }
    }
    console.warn("[tx] persist failed, falling back to memory", err?.name);
    available = false;
    memoryStore.set(key, value);
    return false;
  }
}

export function removeItem(key) {
  memoryStore.delete(key);
  if (!isPersistent()) return;
  try {
    window.localStorage.removeItem(key);
  } catch (err) {
    /* nothing useful to do */
  }
}

function isQuotaError(err) {
  if (!err) return false;
  return (
    err.name === "QuotaExceededError" ||
    err.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    err.code === 22 ||
    err.code === 1014
  );
}

/**
 * Identifies THIS browsing context, so an announcement we made ourselves is not
 * heard back as someone else's write.
 *
 * `BroadcastChannel` excludes only the exact object that posted — and
 * `announce` posts from a throwaway channel — so the subscriber channel in the
 * SAME tab receives every local write. The journal answers a notification by
 * re-reading storage, which hands React a freshly parsed object for every row;
 * consumers key their effects on those objects, so a single rejected withdrawal
 * ran its error effect twice and the user got two identical toasts. jsdom has
 * no `BroadcastChannel` at all, which is why the suite never saw it.
 */
const CONTEXT_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/**
 * Cross-tab change notification. `storage` fires in OTHER tabs only, which is
 * exactly what we want — the writing tab already knows.
 *
 * `BroadcastChannel` is used additionally where supported because the `storage`
 * event can be delayed under load; the journal reconciles by content so
 * receiving both is harmless.
 */
export function subscribe(key, handler) {
  if (typeof window === "undefined") return () => {};

  const onStorage = (event) => {
    if (event.key === null || event.key === key) handler();
  };
  window.addEventListener("storage", onStorage);

  let channel = null;
  if (typeof window.BroadcastChannel === "function") {
    try {
      channel = new window.BroadcastChannel(key);
      channel.onmessage = (event) => {
        // Drop our own echo. A payload without a sender comes from a tab
        // running an older build; treat it as external, which is the safe
        // direction to be wrong in.
        if (event && event.data && event.data.sender === CONTEXT_ID) return;
        handler();
      };
    } catch (err) {
      channel = null;
    }
  }

  return () => {
    window.removeEventListener("storage", onStorage);
    if (channel) {
      try {
        channel.close();
      } catch (err) {
        /* already closed */
      }
    }
  };
}

/** Announce a local write to other tabs via BroadcastChannel (storage fires on its own). */
export function announce(key) {
  if (typeof window === "undefined" || typeof window.BroadcastChannel !== "function") return;
  try {
    const channel = new window.BroadcastChannel(key);
    channel.postMessage({ sender: CONTEXT_ID, at: Date.now() });
    channel.close();
  } catch (err) {
    /* best effort */
  }
}

export const __testing = {
  CONTEXT_ID,
  reset() {
    available = null;
    memoryStore.clear();
  },
  forceMemoryMode() {
    available = false;
  },
  memoryStore,
};
